package listening

import AttemptDetail
import ReadingQuestion
import ReadingSet
import UserProgress
import androidx.compose.runtime.*
import io.github.jan.supabase.auth.auth
import io.github.jan.supabase.postgrest.from
import io.github.jan.supabase.postgrest.query.Order
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import supabase

@Composable
fun ListeningTask3Screen(
    taskId: Int = 13,
    onExit: () -> Unit
) {
    // --- STATE ---
    var currentSet by remember { mutableStateOf<ReadingSet?>(null) }
    var persons by remember { mutableStateOf<List<ReadingQuestion>>(emptyList()) }
    var userAnswers by remember { mutableStateOf<Map<String, String>>(emptyMap()) }
    var isLoading by remember { mutableStateOf(true) }
    var isFinished by remember { mutableStateOf(false) }

    // Для выбора ответа: какую персону сейчас редактируем?
    var activePersonId by remember { mutableStateOf<String?>(null) }

    val startTime = remember { kotlinx.browser.window.performance.now() }
    val scope = rememberCoroutineScope()

    // --- ЗАГРУЗКА ---
    LaunchedEffect(taskId) {
        isLoading = true
        try {
            val currentUser = supabase.auth.currentUserOrNull()

            // 1. Проверяем решенные
            val solvedSetIds = if (currentUser != null) {
                supabase.from("user_progress").select {
                    filter { eq("user_id", currentUser.id); eq("task_id", taskId) }
                }.decodeList<UserProgress>().mapNotNull { it.setId }
            } else emptyList()

            // 2. Загружаем сеты
            val allSets = supabase.from("reading_sets").select {
                filter { eq("task_type", taskId) }
            }.decodeList<ReadingSet>()

            val targetSet = allSets.firstOrNull { it.id !in solvedSetIds }
            currentSet = targetSet

            // 3. Загружаем 8 персон
            if (targetSet != null) {
                persons = supabase.from("reading_items").select {
                    filter { eq("set_id", targetSet.id) }
                    order("order_index", Order.ASCENDING)
                }.decodeList<ReadingQuestion>()
            }
        } catch (e: Exception) {
            console.error("Error Task 3: ${e.message}")
        } finally {
            isLoading = false
        }
    }

    // --- ЗАВЕРШЕНИЕ ---
    fun finishTask() {
        if (currentSet == null) return
        isFinished = true

        val correctCount = persons.count { userAnswers[it.id] == it.correctOption }
        val duration = ((kotlinx.browser.window.performance.now() - startTime) / 1000).toInt()

        val details = persons.map { p ->
            AttemptDetail(
                question = p.question, // "Persona 1"
                userAnswer = userAnswers[p.id] ?: "-",
                correctAnswer = p.correctOption,
                isCorrect = userAnswers[p.id] == p.correctOption
            )
        }

        scope.launch {
            DataRepository.saveProgress(taskId, currentSet!!.id, correctCount, persons.size)
            DataRepository.saveAttempt(
                taskId = taskId,
                setTitle = currentSet!!.title,
                score = correctCount,
                total = persons.size,
                durationSeconds = duration,
                details = details
            )
        }
    }

    // --- UI ---
    Div({
        style {
            padding(20.px); maxWidth(1000.px); property("margin", "0 auto")
            fontFamily("Roboto, sans-serif")
        }
    }) {
        if (isLoading) {
            H3 { Text("⏳ Cargando Tarea 3...") }
            return@Div
        }
        if (persons.isEmpty()) {
            H3 { Text("🎉 No hay más tareas disponibles.") }
            Button({ onClick { onExit() } }) { Text("Volver") }
            return@Div
        }

        val set = currentSet!!
        val optionsPool = set.optionsPool ?: emptyList()

        // Кнопка выхода
        Button({ onClick { onExit() }; style { marginBottom(15.px); cursor("pointer") } }) { Text("⬅ Salir") }

        // Аудио плеер
        set.audioUrl?.let { url ->
            Div({ style { position(Position.Sticky); top(10.px); property("z-index", "100"); backgroundColor(Color.white); padding(10.px); borderRadius(10.px); property("box-shadow", "0 4px 10px rgba(0,0,0,0.1)"); marginBottom(20.px) } }) {
                H4({ style { margin(0.px); marginBottom(5.px) } }) { Text("🎧 Escuche a las 8 personas:") }
                Audio({
                    attr("controls", "")
                    style { width(100.percent) }
                }) { Source({ attr("src", url) }) }
            }
        }

        // --- ЛЕЙАУТ: ДВЕ КОЛОНКИ (Опции слева/сверху, Персоны справа/снизу) ---
        Div({
            style {
                display(DisplayStyle.Flex)
                property("gap", "20px")
                property("flex-wrap", "wrap") // Адаптивность для мобильных
            }
        }) {
            // 1. СПИСОК ОПЦИЙ (A-K)
            Div({
                style {
                    flex(1); minWidth(300.px)
                    backgroundColor(Color.white); padding(20.px); borderRadius(12.px)
                    property("box-shadow", "0 2px 10px rgba(0,0,0,0.05)")
                    property("height", "fit-content")
                }
            }) {
                H4 { Text("Opciones Disponibles:") }
                optionsPool.forEach { opt ->
                    // Проверяем, выбрана ли уже эта опция кем-то (кроме текущего активного, если есть)
                    val isUsed = userAnswers.values.contains(opt.id)

                    Div({
                        style {
                            padding(10.px); marginBottom(8.px); borderRadius(6.px)
                            backgroundColor(if (isUsed) Color("#f0f0f0") else Color("#fef9e7"))
                            border(1.px, LineStyle.Solid, if (isUsed) Color("#ddd") else Color("#f1c40f"))
                            color(if (isUsed) Color("#aaa") else Color("#2c3e50"))
                            fontSize(0.9.cssRem)
                            if (isUsed) textDecoration("line-through")
                        }
                    }) {
                        Span({ style { fontWeight("bold"); marginRight(8.px) } }) { Text("${opt.id}.") }
                        Text(opt.text)
                    }
                }
            }

            // 2. СПИСОК ПЕРСОН (Persona 1..8)
            Div({
                style {
                    flex(1); minWidth(300.px)
                    display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); property("gap", "10px")
                }
            }) {
                H4 { Text("Relaciona cada persona con una frase:") }

                persons.forEach { person ->
                    val selectedOptionId = userAnswers[person.id]
                    val isCorrect = person.correctOption == selectedOptionId

                    Div({
                        style {
                            backgroundColor(Color.white); padding(15.px); borderRadius(10.px)
                            property("box-shadow", "0 2px 5px rgba(0,0,0,0.05)")
                            display(DisplayStyle.Flex); alignItems(AlignItems.Center); justifyContent(JustifyContent.SpaceBetween)

                            // Подсветка при завершении
                            if (isFinished) {
                                if (isCorrect) border(2.px, LineStyle.Solid, Color("#27ae60"))
                                else border(2.px, LineStyle.Solid, Color("#e74c3c"))
                            }
                        }
                    }) {
                        Span({ style { fontWeight("bold"); fontSize(1.1.cssRem) } }) { Text(person.question) } // "Persona 1"

                        // Кнопка выбора
                        Button({
                            onClick { if (!isFinished) activePersonId = person.id }
                            style {
                                padding(8.px, 20.px); borderRadius(20.px); cursor(if (isFinished) "default" else "pointer")
                                border(1.px, LineStyle.Solid, Color("#bdc3c7"))
                                backgroundColor(if (selectedOptionId != null) Color("#f1c40f") else Color.white)
                                color(if (selectedOptionId != null) Color.white else Color("#2c3e50"))
                                fontWeight("bold")
                            }
                        }) {
                            Text(selectedOptionId ?: "Seleccionar...")
                        }
                    }
                }
            }
        }

        // --- МОДАЛЬНОЕ ОКНО (ИЛИ ПАНЕЛЬ) ВЫБОРА ОПЦИИ ---
        if (activePersonId != null && !isFinished) {
            Div({
                style {
                    position(Position.Fixed); top(0.px); left(0.px); width(100.percent); height(100.vh)
                    backgroundColor(Color("rgba(0,0,0,0.5)")); property("z-index", "1000")
                    display(DisplayStyle.Flex); justifyContent(JustifyContent.Center); alignItems(AlignItems.Center)
                }
                // Клик по фону закрывает
                onClick { activePersonId = null }
            }) {
                Div({
                    // Чтобы клик по самому окну не закрывал его
                    onClick { it.stopPropagation() }
                    style {
                        backgroundColor(Color.white); padding(25.px); borderRadius(12.px); maxWidth(500.px); width(90.percent)
                        maxHeight(80.vh); property("overflow-y", "auto")
                    }
                }) {
                    H3({ style { marginTop(0.px) } }) { Text("Elige la opción para la Persona:") }

                    optionsPool.forEach { opt ->
                        // Нельзя выбрать уже занятую (если это не та же самая персона)
                        val isTaken = userAnswers.values.contains(opt.id) && userAnswers[activePersonId] != opt.id

                        Div({
                            onClick {
                                if (!isTaken) {
                                    userAnswers = userAnswers + (activePersonId!! to opt.id)
                                    activePersonId = null // Закрыть
                                }
                            }
                            style {
                                padding(15.px); marginBottom(10.px); borderRadius(8.px); cursor(if (isTaken) "not-allowed" else "pointer")
                                border(1.px, LineStyle.Solid, if (isTaken) Color("#eee") else Color("#ddd"))
                                backgroundColor(if (isTaken) Color("#f9f9f9") else Color.white)
                                color(if (isTaken) Color("#ccc") else Color("#2c3e50"))

                                if (!isTaken) {
                                    property("transition", "background 0.2s")
                                    // hover effect через style нельзя, но логика понятна
                                }
                            }
                        }) {
                            Span({ style { fontWeight("bold"); marginRight(10.px); color(if (isTaken) Color("#ccc") else Color("#f39c12")) } }) {
                                Text(opt.id)
                            }
                            Text(opt.text)
                        }
                    }

                    Button({
                        onClick { activePersonId = null }
                        style { marginTop(10.px); width(100.percent); padding(10.px); cursor("pointer") }
                    }) { Text("Cancelar") }
                }
            }
        }

        // --- ИТОГ ---
        if (!isFinished) {
            Button({
                style { marginTop(30.px); width(100.percent); padding(15.px); backgroundColor(Color("#2c3e50")); color(Color.white); border(0.px); borderRadius(8.px); fontSize(1.2.cssRem); cursor("pointer") }
                onClick { finishTask() }
            }) { Text("Finalizar Tarea 3") }
        } else {
            val score = persons.count { userAnswers[it.id] == it.correctOption }
            Div({
                style {
                    marginTop(20.px); padding(20.px); backgroundColor(Color("#f1c40f")); color(Color.white)
                    borderRadius(10.px); textAlign("center"); fontWeight("bold"); fontSize(1.5.cssRem)
                }
            }) { Text("Resultado: $score / ${persons.size}") }
        }
    }
}